/**
 * order-block.ts — why an order cannot go, as the button that would send it
 * says it: a translation key and its values, which SpendButton reads
 * (components/game/common/spend-button.tsx). The rules that give one live
 * with their orders — capital.ts for debt and shares, the Fleet page for
 * aircraft — and the budget's and the team cash's, which spends share, live
 * here.
 *
 * An order the server refused comes back as an OrderRefusal, which the order's
 * domain reads by its code into what the player is told (refusalText).
 */

import i18n from '../i18n/config';
import { formatCash } from '../utils/format';

export interface OrderBlock {
  key: string;
  values?: Record<string, string | number>;
}

/**
 * An order the server refused, as it arrives: the refusal in English, for the
 * log, and, when a player can meet it in play, the code the domain's
 * *_REFUSALS table names it by, with the figures that code carries.
 */
export interface OrderRefusal {
  /** Shown as it came when there is no code the client knows. */
  error: string;
  reason?: string;
  need?: number;
  have?: number;
  /** The operating cycles until a study can run again (a commercial cooldown). */
  cycles?: number;
  /** The city a contract needs flights to (a commercial frequency). */
  destination_id?: number;
}

/**
 * What a refusal's code names in its domain's *_REFUSALS table — the key that
 * says it, or a join refusal's whole look — or undefined when the table does
 * not name it: no code, a code from a newer server, or a name every object
 * answers to (`toString`) rather than the table's own.
 */
export function refusalKey<T extends Record<string, unknown>>(table: T, reason: string | undefined): T[keyof T] | undefined {
  return reason !== undefined && Object.hasOwn(table, reason) ? table[reason as keyof T] : undefined;
}

/**
 * What a refusal tells the player: its reason in the player's language when
 * the domain read one from its code, else the server's words as they came.
 */
export function refusalText(block: OrderBlock | null, refusal: OrderRefusal): string {
  return block ? i18n.t(block.key, block.values) : refusal.error;
}

/** Room for the float error in figures held in millions. */
const EPSILON = 1e-9;

/**
 * Your budget short of an order's price: a spend comes out of the budget of
 * whoever makes it. Null while your budget is not known, or covers it.
 */
export function budgetBlock(budget: number | undefined, price: number): OrderBlock | null {
  if (budget === undefined || price <= budget + EPSILON) return null;
  return { key: 'common.budgetReason', values: { budget: formatCash(budget), price: formatCash(price) } };
}

/**
 * The team's cash short of an order's price. The server checks it before the
 * buyer's budget — when the cash is short no budget would buy either — so a
 * button checks it first too. Null while the cash is not known, or covers it.
 */
export function cashBlock(cash: number | undefined, price: number): OrderBlock | null {
  if (cash === undefined || price <= cash + EPSILON) return null;
  return { key: 'common.cashReason', values: { cash: formatCash(cash), price: formatCash(price) } };
}
